<template>
    <page>
        <div class="shadow-25">
            <v-header :title="$t('upload and import file')" icon="cloud upload"></v-header>
            <card width="800px">

                <alert :trigger="success"
                       color="positive"
                       icon="done"
                       :html="responseMessage"></alert>

                <alert :trigger="success === false"
                       color="negative"
                       icon="error"
                       :html="$T('unable to import this file!')"></alert>

                <form @submit.prevent="upload"
                      class="q-pt-xs gutter-xs row">

                    <v-radio path="delimiter"
                             v-model="form.delimiter"
                             :label="$T('delimiter')"
                             :helper="$T('the character used to separate columns.')"
                             :options="delimiterOptions"
                             :form="form"
                             class="col-12">
                    </v-radio>

                    <v-select v-for="(column, i) in csvColumns"
                              :key="i"
                              :path="`column${i}`"
                              v-model="form[`column${i}`]"
                              :label="$_t('the column :i is the field', {i: i+1})"
                              :options="csvColumns"
                              :clearable="false"
                              :form="form"
                              class="col-12">
                    </v-select>

                    <!-- File -->
                    <v-file ref="file"
                            path="file"
                            :label="$t('file')"
                            :url="url"
                            extensions=".csv"
                            color="highlight"
                            :helper="$T('save a csv file and upload it here.')"
                            @uploaded="receiveData"
                            :additional-fields="serializedForm"
                            :title="$T('select file')"
                            class="col-12">
                    </v-file>

                    <v-checkbox path="skip_first"
                                v-model="form.skip_first"
                                :label="$t('skip first line')"
                                :form="form"
                                class="col-12">
                    </v-checkbox>

                    <!-- Submit Button -->
                    <div class="row justify-center col-12 q-mt-md group">
                        <v-button outline
                                  :label="$t('download example')"
                                  icon="grid_on"
                                  color="info"
                                  class="no-shadow q-btn-iqos"
                                  @click.prevent="downloadSample('lending_kits_to_change_status.csv')"
                                  type="button">
                        </v-button>

                        <v-button outline
                                  :label="$t('send')"
                                  icon="send"
                                  color="highlight"
                                  class="no-shadow q-btn-iqos"
                                  :loading="uploading"
                                  type="submit">
                            <template slot="loading">
                                <q-spinner-puff class="on-left" color="white"></q-spinner-puff>
                                {{ $t('sending...') | capitalize }}
                            </template>
                        </v-button>

                        <p v-if="uploading" class="col-12 q-mt-sm text-center text-negative">
                            {{ $T('the file will be processed after the upload.') }}
                            {{ $T('this could take a while until the imported data shows up in the list.') }}
                        </p>
                    </div>

                </form>

            </card>
        </div>

        <q-modal v-model="dragModal"
                 @drop.native.prevent.stop="onDrop"
                 @dragleave.native.prevent.stop="onDragLeave"
                 content-classes="row items-center justify-center upload-drop-zone">
            <h2 class="q-display-1 text-center">
                <q-icon name="cloud upload" color="faded" size="3em"></q-icon>
                <br>
                {{ $t('drop your file here') }}
            </h2>
        </q-modal>
    </page>
</template>

<script>
    import {
        QModal,
        QIcon,
        openURL
    } from 'quasar-framework'
    import Form from 'vform'
    import {mapGetters} from "vuex";

    export default {
        middleware: 'auth',

        metaInfo() {
            return {title: this.$T('upload and import file')}
        },

        components: {
            QModal,
            QIcon
        },

        data() {
            let csvColumns = [
                {label: this.$T('codentify'), value: 'codentify'},
            ]
            let delimiterOptions = [
                {label: this.$T('semicolon (;)'), value: ';'},
                {label: this.$T('comma (,)'), value: ','}
            ]
            let form = {
                skip_first: true,
                delimiter: ';'
            }

            csvColumns.forEach((column, i) => form[`column${i}`] = column.value)

            return {
                uploader: {},
                dragModal: false,
                success: null,
                csvColumns,
                delimiterOptions,
                form: new Form(form),
                responseMessage: "",
            }
        },

        mounted() {
            this.uploader = this.$refs.file.$refs.uploader

            // Add listener for drag into the window
            window.addEventListener('dragover', this.onDragOver)
        },

        destroyed() {
            // Remove listener
            window.removeEventListener('dragover', this.onDragOver)
        },

        computed: {
            url() {
                return `${config.appUrl}/api/v1/lending-kit/import/change-status`
            },

            uploading() {
                return this.uploader.uploading
            },

            serializedForm() {
                return Object.keys(this.form).map(name => ({name, value: this.form[name]}))
            }
        },

        methods: {
            onDrop(e) {
                this.uploader.__onDrop(e)
                this.dragModal = false
            },

            onDragOver(e) {
                e.stopPropagation()
                e.preventDefault()
                this.dragModal = true
            },

            onDragLeave(e) {
                this.dragModal = false
            },

            upload() {
                //Resets the success state
                this.success = null

                // Submit the file
                this.uploader.upload()
            },

            receiveData(xhr) {
                // Get the response string (it's a common xhr request. The data is a string in response)
                var responseData = null

                // Tries to parse the data
                try {
                    responseData = JSON.parse(xhr.response)
                } catch (e) {
                }

                if (responseData && responseData.success) {
                    //Success (Triggers a positive alert)
                    this.success = true
                    this.responseMessage = responseData.success.message
                }
                // If no form data or formData, alert the user
                else {
                    if (responseData.error) {
                        this.$q.notify({
                            message: responseData.error.message,
                            type: 'negative',
                            icon: 'warning',
                            position: 'top',
                            timeout: 0,
                            closeBtn: true
                        })
                    } else {
                        if (responseData.errors) {
                            this.form.errors.errors = responseData.errors
                        }
                        //Error (=not success. Triggers a negative alert)
                        this.success = false
                    }
                }

                // Clear the files object in the uploader (the reset method triggers some xhr unwanted behavior)
                this.uploader.files = []
            },

            downloadSample(fileName) {
                openURL(`${window.config.appUrl}/samples/${fileName}`)
            }
        }
    }
</script>



// WEBPACK FOOTER //
// resources/assets/js/markets/AD/pages/backoffice/lending-kit/import.vue